import type { CSSProperties } from "react";
import type { IComponentProperty, IContentComponent, IEditorFrameChrome, IEditorFrameLayer, IEditorSiteFrame, IScriptAndStyle } from "../../../models/Interfaces";

export const EDITOR_FRAME_ATTR = "data-sbcms-editor-frame";
export const EDITOR_CONTENT_ATTR = "data-sbcms-editor-content";
export const EDITOR_CHROME_ATTR = "data-sbcms-editor-chrome";

const FRAME_TAGS = new Set(["div", "header", "footer", "section", "nav", "main", "article", "aside"]);

const NEUTRAL_EDITOR_STYLE = `
  body {
    font-family: "Open Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    font-size: 1rem;
    color: #1f2a3d;
    line-height: 1.6;
    padding: 0.5rem 0.65rem;
    background-color: #f6f9fd;
  }
  p, div, li {
    font-size: 1rem;
    line-height: 1.6;
  }
  a {
    color: #245497;
  }
`;

export interface IEnabledSiteStyles {
  css: string;
  urls: string[];
}

export const hasDeclaredStyle = (style?: CSSProperties): boolean => {
  if (!style) {
    return false;
  }

  return Object.values(style).some((value) => value !== undefined && value !== "");
};

const usableLayer = (layer: IEditorFrameLayer): boolean => {
  return Boolean(layer.className?.trim() || layer.id?.trim() || hasDeclaredStyle(layer.style));
};

export const frameHasLayers = (frame?: IEditorSiteFrame | null): boolean => {
  return (frame?.layers ?? []).some(usableLayer);
};

export const joinClasses = (...parts: Array<string | null | undefined>): string => {
  return parts
    .flatMap((part) => (part ?? "").split(/\s+/))
    .filter(Boolean)
    .join(" ");
};

const normalizeName = (value?: string | null): string => {
  return (value ?? "").replace(/\s+/g, "").toLowerCase();
};

const escapeAttr = (value: string): string => {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
};

const cssPropertyName = (key: string): string => {
  if (key.startsWith("--")) {
    return key;
  }

  return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
};

export const cssPropertiesToInline = (style?: CSSProperties): string => {
  if (!style) {
    return "";
  }

  return Object.entries(style)
    .filter(([, value]) => value !== undefined && value !== "")
    .map(([key, value]) => `${cssPropertyName(key)}: ${String(value)}`)
    .join("; ");
};

const layer = (className?: string, style?: CSSProperties, id?: string): IEditorFrameLayer[] => {
  const name = className?.trim() ?? "";
  const next: IEditorFrameLayer = {
    className: name || undefined,
    id: id?.trim() || undefined,
    style: hasDeclaredStyle(style) ? style : undefined
  };

  return usableLayer(next) ? [next] : [];
};

export const escapeStyleClose = (css: string): string => {
  return css.replace(/<\/style/gi, "<\\/style");
};

export const buildEditorContentStyle = (options: {
  siteCss?: string;
  pageCss?: string;
  themed: boolean;
}): string => {
  if (!options.themed) {
    return NEUTRAL_EDITOR_STYLE;
  }

  const frame = `
    html { height: 100%; }
    body { margin: 0; min-height: 100%; }
    [${EDITOR_FRAME_ATTR}] { min-height: 100%; box-sizing: border-box; }
  `;

  return [frame, escapeStyleClose(options.siteCss ?? ""), escapeStyleClose(options.pageCss ?? "")]
    .filter((part) => part.trim().length > 0)
    .join("\n");
};

export const enabledStylesheetText = (sheets: Array<Pick<IScriptAndStyle, "Enabled" | "Code" | "LoadOrder" | "Url">>): IEnabledSiteStyles => {
  const enabled = [...sheets]
    .filter((sheet) => sheet.Enabled)
    .sort((left, right) => (left.LoadOrder ?? 0) - (right.LoadOrder ?? 0));

  const css = enabled
    .map((sheet) => sheet.Code?.trim() ?? "")
    .filter(Boolean)
    .join("\n");

  const urls = enabled
    .filter((sheet) => !sheet.Code?.trim() && /^https?:\/\//i.test(sheet.Url?.trim() ?? ""))
    .map((sheet) => sheet.Url!.trim());

  return { css, urls };
};

let pendingStyles: { key: string; task: Promise<IEnabledSiteStyles> } | null = null;

export const loadSiteEditorStyles = (
  siteKey: string,
  load: () => Promise<IScriptAndStyle[]>
): Promise<IEnabledSiteStyles> => {
  if (pendingStyles?.key === siteKey) {
    return pendingStyles.task;
  }

  const task = load()
    .then((sheets) => enabledStylesheetText(sheets))
    .catch(() => ({ css: "", urls: [] as string[] }));

  pendingStyles = { key: siteKey, task };
  task.finally(() => {
    if (pendingStyles?.task === task) {
      pendingStyles = null;
    }
  });

  return task;
};

export const buildPageSectionFrame = (input: {
  pageClass?: string | null;
  pageStyle?: CSSProperties;
  pageCss?: string | null;
  sectionClass?: string | null;
  sectionId?: string | null;
  sectionStyle?: CSSProperties;
}): IEditorSiteFrame => {
  const layers: IEditorFrameLayer[] = [];
  const pageClass = input.pageClass?.trim() ?? "";
  const pageStyle: CSSProperties = { ...(input.pageStyle ?? {}) };

  if (pageClass || hasDeclaredStyle(pageStyle)) {
    if (!pageStyle.minHeight) {
      pageStyle.minHeight = "100%";
    }
    layers.push({ className: pageClass || undefined, style: pageStyle });
  }

  const sectionClass = input.sectionClass?.trim() ?? "";
  const sectionId = input.sectionId?.trim() ?? "";

  if (sectionClass || sectionId || hasDeclaredStyle(input.sectionStyle)) {
    layers.push({
      className: sectionClass || undefined,
      id: sectionId || undefined,
      style: hasDeclaredStyle(input.sectionStyle) ? input.sectionStyle : undefined
    });
  }

  const pageCss = input.pageCss?.trim() ?? "";

  return {
    layers,
    pageCss: pageCss || undefined
  };
};

const classValue = (classes: Record<string, string> | undefined, name: string): string => {
  if (!classes) {
    return "";
  }

  const found = Object.keys(classes).find((key) => key.toLowerCase() === name.toLowerCase());
  return found ? classes[found].trim() : "";
};

export const componentClassMap = (component: IContentComponent): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const prop of component.BaseComponent?.Properties ?? []) {
    const name = prop.PropertyName?.trim();
    if (!name) {
      continue;
    }

    const value = (prop.PublishedValue ?? prop.DefaultValue ?? "").trim();
    if (value) {
      map[name] = value;
    }
  }

  return map;
};

export const extendEditorFrame = (
  frame: IEditorSiteFrame | undefined,
  extra: IEditorFrameLayer[]
): IEditorSiteFrame => {
  return {
    layers: [...(frame?.layers ?? []), ...extra.filter(usableLayer)],
    pageCss: frame?.pageCss,
    classes: frame?.classes,
    chrome: frame?.chrome
  };
};

const colourStyle = (background?: string | null, color?: string | null): CSSProperties | undefined => {
  const style: CSSProperties = {};

  if (background) {
    style.backgroundColor = background;
  }

  if (color) {
    style.color = color;
  }

  return hasDeclaredStyle(style) ? style : undefined;
};

export const layersForProperty = (
  componentName: string | undefined,
  propertyName: string | undefined,
  typeName: string | undefined,
  classes: Record<string, string> | undefined
): IEditorFrameLayer[] => {
  const componentKey = normalizeName(componentName);
  const propertyKey = normalizeName(propertyName);
  const typeKey = normalizeName(typeName);
  const cls = (name: string) => classValue(classes, name);

  if (typeKey === "htmlitems" || (componentKey === "htmlblock" && propertyKey === "items")) {
    return layer(joinClasses("cms-htmlblock-container", cls("ContainerClass")));
  }

  if (typeKey === "accordionitems" || propertyKey === "accordiondata") {
    return layer(joinClasses("cms-accordion-container", cls("ContainerClass")));
  }

  if (typeKey === "tabitems" || (componentKey === "tabbedcontent" && propertyKey === "tabs")) {
    return layer(joinClasses("cms-tabs-container", cls("ContainerClass")));
  }

  if (typeKey === "carousel" || (componentKey === "carousel" && propertyKey === "items")) {
    return layer(joinClasses("text-center cms-carousel-container", cls("CarouselContainerClass")));
  }

  if (typeKey !== "bodytext") {
    return [];
  }

  switch (componentKey) {
    case "bodytext":
      return [
        ...layer(joinClasses("cms-bodytext-container", cls("ContainerClass"))),
        ...layer(joinClasses("cms-bodytext-content", cls("BodyClass")))
      ];
    case "newsarticle":
      if (propertyKey === "title") {
        return [
          ...layer(joinClasses("col-12 p-0 m-0 cms-article-title-container", cls("TitleContainerClass"))),
          ...layer(joinClasses(cls("TitleClass"), "cms-article-title"))
        ];
      }
      if (propertyKey === "intro") {
        return [
          ...layer(joinClasses("col-12 p-0 m-0 cms-article-intro-container", cls("IntroContainerClass"))),
          ...layer(joinClasses("cms-article-intro", cls("IntroClass")))
        ];
      }
      if (propertyKey === "body") {
        return layer(joinClasses("col-12 p-0 m-0 cms-article-body-container", cls("BodyContainerClass")));
      }
      return [];
    case "newssummary":
      if (propertyKey === "title") {
        return [
          ...layer(joinClasses("col-12 m-0 p-0 cms-newssummary-titlecontainer", cls("TitleContainerClass"))),
          ...layer(joinClasses("cms-newssummary-title", cls("TitleClass")))
        ];
      }
      return [];
    case "banner":
      return [
        ...layer(
          joinClasses("d-flex align-items-center cms-banner-container", cls("BannerClass")),
          colourStyle(cls("BackColour"), cls("TextColour"))
        ),
        ...layer(joinClasses("m-0 p-0 h-100 cms-banner-text", cls("TextContainerClass")))
      ];
    case "card":
      return [
        ...layer(joinClasses("cms-card-container", cls("CardContainerClass"))),
        ...layer(joinClasses("card cms-card", cls("CardClass"))),
        ...layer(joinClasses("card-body cms-card-body", cls("BodyClass"))),
        ...layer("card-text cms-card-text")
      ];
    case "titleandtext":
      return [
        ...layer(joinClasses("cms-titletext-container", cls("ContainerClass"))),
        ...layer(joinClasses("cms-titletext-text-container", cls("ContentContainer"))),
        ...layer(joinClasses("cms-titletext-body", cls("BodyClass")))
      ];
    case "accordion":
      if (propertyKey === "title") {
        return [
          ...layer(joinClasses("cms-accordion-container", cls("ContainerClass"))),
          ...layer(joinClasses("col-12 cms-accordion-title", cls("TitleClass")))
        ];
      }
      return [];
    case "gallery":
      if (propertyKey === "title") {
        return [
          ...layer("cms-gallery-container"),
          ...layer(joinClasses("cms-gallery-title", cls("TitleClass")))
        ];
      }
      return [];
    case "video":
      if (propertyKey === "title") {
        return [
          ...layer(joinClasses("cms-video-container", cls("ContainerClass"))),
          ...layer(joinClasses("text-center cms-video-title", cls("TitleClass")))
        ];
      }
      return [];
    case "image":
      if (propertyKey === "caption") {
        return [
          ...layer(joinClasses("cms-image-container", cls("ImageContainerClass"))),
          ...layer(joinClasses("cms-image-caption-container", cls("CaptionContainerClass"))),
          ...layer(joinClasses("cms-image-caption", cls("CaptionClass")))
        ];
      }
      return [];
    case "contactprofile":
      if (propertyKey === "infotext") {
        return [
          ...layer(joinClasses("cms-profile-card-container", cls("ContainerClass"))),
          ...layer(joinClasses("cms-profile-info-container", cls("InfoContainerClass")))
        ];
      }
      return [];
    case "subscribe":
      if (propertyKey === "contentblock") {
        return [
          ...layer(joinClasses("cms-subscribe-container", cls("ContainerClass"))),
          ...layer("col-12 cms-subscribe-contentcontainer")
        ];
      }
      return [];
    case "forms":
      if (propertyKey === "submissionsuccesstext") {
        return [
          ...layer(joinClasses("cms-form-container", cls("ContainerClass"))),
          ...layer(joinClasses(cls("SubmissionSuccessContainerClass") || "alert-success", "text-center cms-form-success-container"))
        ];
      }
      if (propertyKey === "submissionerrortext") {
        return [
          ...layer(joinClasses("cms-form-container", cls("ContainerClass"))),
          ...layer(joinClasses(cls("SubmissionErrorContainerClass") || "alert-danger", "text-center cms-form-error-container"))
        ];
      }
      return [];
    default:
      return [];
  }
};

export const frameForProperty = (
  pageFrame: IEditorSiteFrame | undefined,
  component: IContentComponent,
  property: IComponentProperty
): IEditorSiteFrame => {
  const classes = { ...(pageFrame?.classes ?? {}), ...componentClassMap(component) };

  return {
    layers: [
      ...(pageFrame?.layers ?? []),
      ...layersForProperty(component.BaseComponent?.Name, property.PropertyName, property.Type?.Name, classes)
    ].filter(usableLayer),
    pageCss: pageFrame?.pageCss,
    classes
  };
};

export const htmlBlockItemFrame = (
  frame: IEditorSiteFrame | undefined,
  backgroundColour?: string | null
): IEditorSiteFrame => {
  const background = backgroundColour?.trim();

  return extendEditorFrame(frame, [
    ...layer(
      joinClasses("cms-htmlblock-item-container", classValue(frame?.classes, "ItemContainerClass")),
      background ? { backgroundColor: background } : undefined
    ),
    ...layer(joinClasses("cms-htmlblock-content-container", classValue(frame?.classes, "ContentContainerClass")))
  ]);
};

export const accordionItemFrame = (
  frame: IEditorSiteFrame | undefined,
  part: "title" | "content"
): IEditorSiteFrame => {
  if (part === "title") {
    return extendEditorFrame(frame, layer(
      joinClasses("accordion-header cms-accordion-header", classValue(frame?.classes, "ItemTitleClass"))
    ));
  }

  return extendEditorFrame(frame, layer(
    joinClasses("accordion-body cms-accordion-body", classValue(frame?.classes, "ItemContentClass"))
  ));
};

export const tabItemFrame = (
  frame: IEditorSiteFrame | undefined,
  part: "title" | "content",
  item: { TitleClass?: string | null; ContentContainer?: string | null; BackgroundColour?: string | null }
): IEditorSiteFrame => {
  if (part === "title") {
    return extendEditorFrame(frame, layer(joinClasses("nav-link cms-nav-link", item.TitleClass)));
  }

  const background = item.BackgroundColour?.trim();

  return extendEditorFrame(frame, [
    ...layer("tab-content cms-tab-content"),
    ...layer(
      joinClasses("tab-pane cms-tab-pane active show", item.ContentContainer),
      background ? { backgroundColor: background } : undefined
    )
  ]);
};

export const carouselCaptionFrame = (
  frame: IEditorSiteFrame | undefined,
  part: "title" | "text"
): IEditorSiteFrame => {
  const caption = layer(joinClasses(
    "carousel-caption cms-carousel-caption-container",
    classValue(frame?.classes, "CaptionContainerClass")
  ));

  if (part === "title") {
    return extendEditorFrame(frame, [
      ...caption,
      ...layer(joinClasses("cms-carousel-caption-title-container", classValue(frame?.classes, "CaptionTitleContainerClass"))),
      ...layer(joinClasses("cms-carousel-caption-title", classValue(frame?.classes, "CaptionTitleClass")))
    ]);
  }

  return extendEditorFrame(frame, [
    ...caption,
    ...layer(joinClasses("cms-carousel-caption-text-container", classValue(frame?.classes, "CaptionTextContainerClass"))),
    ...layer(joinClasses("fontsize-carouseltext cms-carousel-caption-text", classValue(frame?.classes, "CaptionTextClass")))
  ]);
};

export interface IHeaderPreviewSource {
  HeaderTextContainerClass?: string | null;
}

export interface IFooterPreviewSource {
  ContainerClass?: string | null;
  BackgroundColour?: string | null;
  TextColour?: string | null;
  StyledColumn1?: string | null;
  Column1BackgroundColour?: string | null;
  Column1TextColour?: string | null;
  StyledColumn2?: string | null;
  Column2BackgroundColour?: string | null;
  Column2TextColour?: string | null;
  StyledColumn3?: string | null;
  Column3BackgroundColour?: string | null;
  Column3TextColour?: string | null;
  CopyrightNotice?: string | null;
  CopyrightBackgroundColour?: string | null;
  CopyrightTextColour?: string | null;
}

export type FooterPreviewField = "StyledColumn1" | "StyledColumn2" | "StyledColumn3" | "CopyrightNotice";

const FOOTER_TOP_CLASS = "footer-top";

const frameTag = (tag?: string): string => {
  const name = (tag ?? "div").trim().toLowerCase();
  return FRAME_TAGS.has(name) ? name : "div";
};

const hasClassToken = (html: string | null | undefined, token: string): boolean => {
  if (!html) {
    return false;
  }

  const pattern = /class\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(html)) !== null) {
    const classes = (match[1] ?? match[2] ?? "").split(/\s+/);
    if (classes.includes(token)) {
      return true;
    }
  }

  return false;
};

const footerFieldHtml = (footer: IFooterPreviewSource, field: FooterPreviewField): string | null | undefined => {
  if (field === "StyledColumn1") {
    return footer.StyledColumn1;
  }

  if (field === "StyledColumn2") {
    return footer.StyledColumn2;
  }

  if (field === "StyledColumn3") {
    return footer.StyledColumn3;
  }

  return footer.CopyrightNotice;
};

/**
 * Some themes style the whole footer with footer.cms-footer:has(.footer-top),
 * while that class lives in only one stored field. The marker is added for the
 * other fields, and only when this footer already uses the class.
 */
const footerTopChrome = (footer: IFooterPreviewSource, field: FooterPreviewField): IEditorFrameChrome[] | undefined => {
  const fields = [footer.StyledColumn1, footer.StyledColumn2, footer.StyledColumn3, footer.CopyrightNotice];
  const recordUsesClass = fields.some((html) => hasClassToken(html, FOOTER_TOP_CLASS));

  if (!recordUsesClass || hasClassToken(footerFieldHtml(footer, field), FOOTER_TOP_CLASS)) {
    return undefined;
  }

  return [{ className: FOOTER_TOP_CLASS }];
};

const footerShell = (footer: IFooterPreviewSource): IEditorFrameLayer[] => {
  return [
    { tag: "footer", className: "cms-footer mt-auto" },
    ...layer(undefined, colourStyle(footer.BackgroundColour, footer.TextColour)),
    ...layer(joinClasses(footer.ContainerClass, "cms-footer-container"))
  ];
};

const footerColumnLayers = (
  footer: IFooterPreviewSource,
  field: Exclude<FooterPreviewField, "CopyrightNotice">
): IEditorFrameLayer[] => {
  if (field === "StyledColumn1") {
    return [
      ...layer("row p-0 m-0"),
      ...layer(
        "col-md-4 col-xs-12 p-0 m-0 cms-footer-left-col",
        colourStyle(footer.Column1BackgroundColour, footer.Column1TextColour)
      ),
      ...layer("row m-0"),
      ...layer("col-12 m-0")
    ];
  }

  const middle = field === "StyledColumn2";

  return [
    ...layer("row p-0 m-0"),
    ...layer(
      joinClasses("col-md-4 col-xs-12 p-0 m-0", middle ? "cms-footer-mid-col" : "cms-footer-right-col"),
      colourStyle(
        middle ? footer.Column2BackgroundColour : footer.Column3BackgroundColour,
        middle ? footer.Column2TextColour : footer.Column3TextColour
      )
    )
  ];
};

/** Same wrappers the public header uses around HeaderText. */
export const buildHeaderEditorFrame = (header: IHeaderPreviewSource): IEditorSiteFrame => {
  return {
    layers: [
      { tag: "header", id: "site-header", className: "cms-site-header" },
      { tag: "section", id: "PageHeader" },
      ...layer("row m-0 p-0"),
      ...layer(joinClasses("col-12 m-0 p-0 cms-pageheader-text-container", header.HeaderTextContainerClass))
    ]
  };
};

/** Same wrappers the public footer uses around one stored column or the copyright notice. */
export const buildFooterEditorFrame = (
  footer: IFooterPreviewSource,
  field: FooterPreviewField
): IEditorSiteFrame => {
  const inner = field === "CopyrightNotice"
    ? [
      ...layer(
        "row ps-0 pe-0 cms-footer-copyright-container pt-2 pb-2",
        colourStyle(footer.CopyrightBackgroundColour, footer.CopyrightTextColour)
      ),
      ...layer("col-md-12 ps-0 pe-0 cms-footer-copyright")
    ]
    : footerColumnLayers(footer, field);

  return {
    layers: [...footerShell(footer), ...inner],
    chrome: footerTopChrome(footer, field)
  };
};

const marked = (html: string, attr: string): boolean => {
  return new RegExp(`${attr}\\s*=\\s*["']1["']`).test(html);
};

const renderChrome = (items?: IEditorFrameChrome[]): string => {
  return (items ?? [])
    .filter((item) => item.className?.trim())
    .map((item) => {
      const extra = cssPropertiesToInline(item.style);
      const style = extra ? `display: none !important; ${extra}` : "display: none !important";

      return `<div ${EDITOR_CHROME_ATTR}="1" contenteditable="false" class="${escapeAttr(item.className.trim())}" style="${escapeAttr(style)}">&#8203;</div>`;
    })
    .join("");
};

const stripChrome = (html: string): string => {
  const attr = EDITOR_CHROME_ATTR.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`<([a-zA-Z0-9]+)\\b[^>]*\\b${attr}\\s*=\\s*["']1["'][^>]*>[\\s\\S]*?<\\/\\1>`, "gi");
  return html.replace(pattern, "");
};

export const wrapEditorHtml = (html: string, frame?: IEditorSiteFrame | null): string => {
  const layers = (frame?.layers ?? []).filter(usableLayer);
  const chrome = renderChrome(frame?.chrome);

  if (layers.length === 0 || marked(html, EDITOR_FRAME_ATTR)) {
    return html;
  }

  let inner = html;
  const separateContent = layers.length === 1 && chrome.length > 0;

  if (separateContent) {
    inner = `<div ${EDITOR_CONTENT_ATTR}="1">${inner}</div>`;
  }

  for (let index = layers.length - 1; index >= 0; index -= 1) {
    const item = layers[index];
    const tag = frameTag(item.tag);
    const style = cssPropertiesToInline(item.style);
    const isOuter = index === 0;
    const isContent = index === layers.length - 1 && !separateContent;
    const attrs = [
      isOuter ? `${EDITOR_FRAME_ATTR}="1"` : "",
      isContent ? `${EDITOR_CONTENT_ATTR}="1"` : "",
      item.id ? `id="${escapeAttr(item.id)}"` : "",
      item.className?.trim() ? `class="${escapeAttr(item.className.trim())}"` : "",
      style ? `style="${escapeAttr(style)}"` : ""
    ].filter(Boolean);

    inner = `<${tag} ${attrs.join(" ")}>${isOuter ? chrome : ""}${inner}</${tag}>`;
  }

  return inner;
};

const findMatchingClose = (html: string, from: number, tag: string): number => {
  const lower = html.toLowerCase();
  const openToken = `<${tag.toLowerCase()}`;
  const closeToken = `</${tag.toLowerCase()}`;
  let depth = 1;
  let index = from;

  while (index < html.length) {
    const nextOpen = lower.indexOf(openToken, index);
    const nextClose = lower.indexOf(closeToken, index);

    if (nextClose < 0) {
      return -1;
    }

    if (nextOpen >= 0 && nextOpen < nextClose) {
      const after = lower[nextOpen + openToken.length];
      if (after && /[a-z0-9]/.test(after)) {
        index = nextOpen + openToken.length;
        continue;
      }

      const openEnd = lower.indexOf(">", nextOpen);
      if (openEnd < 0) {
        return -1;
      }

      if (lower[openEnd - 1] !== "/") {
        depth += 1;
      }

      index = openEnd + 1;
      continue;
    }

    const closeEnd = lower.indexOf(">", nextClose);
    if (closeEnd < 0) {
      return -1;
    }

    depth -= 1;
    if (depth === 0) {
      return nextClose;
    }

    index = closeEnd + 1;
  }

  return -1;
};

const extractInnermost = (html: string, attr: string): string | null => {
  const marker = new RegExp(`${attr}\\s*=\\s*["']1["']`, "g");
  let found: string | null = null;
  let match: RegExpExecArray | null;

  while ((match = marker.exec(html)) !== null) {
    const attrAt = match.index;

    const start = html.lastIndexOf("<", attrAt);
    const tagMatch = start >= 0 ? /^<([a-zA-Z0-9]+)/.exec(html.slice(start)) : null;
    const openEnd = html.indexOf(">", attrAt);

    if (!tagMatch || openEnd < 0) {
      break;
    }

    if (html[openEnd - 1] === "/") {
      found = "";
      marker.lastIndex = openEnd + 1;
      continue;
    }

    const innerEnd = findMatchingClose(html, openEnd + 1, tagMatch[1]);
    if (innerEnd < 0) {
      return found;
    }

    found = html.slice(openEnd + 1, innerEnd);
    marker.lastIndex = innerEnd + tagMatch[1].length + 3;
  }

  return found;
};

export const unwrapEditorHtml = (html: string): string => {
  if (!html) {
    return html;
  }

  const inner = marked(html, EDITOR_CONTENT_ATTR)
    ? extractInnermost(html, EDITOR_CONTENT_ATTR) ?? html
    : html;

  return stripChrome(inner);
};
